import React, { useEffect, useState, useCallback, useMemo } from "react";
import { PortalService } from "services/portal";
import { getPortalSocket } from "services/portal/socket";
import { RefreshCw, CheckCircle, AlertCircle, Clock, Play, X, Circle, ChevronDown, ChevronUp } from "lucide-react";

const CARD = { background: "var(--bg-card)", borderRadius: 12, border: "1px solid var(--border)", overflow: "hidden" };
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const STATUS_STYLE = {
  COMPLETED: { bg: "#ecfdf5", fg: "#059669", label: "Completada", Icon: CheckCircle },
  RUNNING: { bg: "#eff6ff", fg: "#2563eb", label: "En curso", Icon: RefreshCw, spin: true },
  PENDING: { bg: "#fef3c7", fg: "#d97706", label: "Pendiente", Icon: Clock },
  FAILED: { bg: "#fef2f2", fg: "#dc2626", label: "Falló", Icon: AlertCircle },
  CANCELLED: { bg: "#f1f5f9", fg: "#64748b", label: "Cancelada", Icon: AlertCircle },
};

// Estado visual por mes en la grilla anual.
const MONTH_TILE = {
  SYNCED:   { bg: "#ecfdf5", border: "#86efac", fg: "#059669", label: "Sincronizado",   Icon: CheckCircle },
  RUNNING:  { bg: "#eff6ff", border: "#93c5fd", fg: "#2563eb", label: "En curso",       Icon: RefreshCw, spin: true },
  FAILED:   { bg: "#fef2f2", border: "#fecaca", fg: "#dc2626", label: "Falló",          Icon: AlertCircle },
  PENDING:  { bg: "#fffbeb", border: "#fde68a", fg: "#d97706", label: "Pendiente",      Icon: Clock },
  FUTURE:   { bg: "#f8fafc", border: "#e2e8f0", fg: "#94a3b8", label: "Período futuro", Icon: Circle },
};

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" });
}

function fmtDuration(start, end) {
  if (!start) return "—";
  const ms = (end ? new Date(end) : new Date()).getTime() - new Date(start).getTime();
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

export function PortalSincronizaciones() {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1-12
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  // triggering: { month, year } | null mientras se dispara un sync puntual
  const [triggering, setTriggering] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await PortalService.getSyncHistory();
      setHistory(Array.isArray(data) ? data : data?.syncs || []);
    } catch (err) {
      setFeedback({ kind: "error", msg: err?.response?.data?.error || err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresca historial cuando termine un sync (vía socket).
  useEffect(() => {
    const socket = getPortalSocket();
    if (!socket) return;
    const onDone = () => load();
    const onError = () => load();
    const onStart = () => load();
    socket.on("sync:done", onDone);
    socket.on("sync:error", onError);
    socket.on("sync:start", onStart);
    return () => {
      socket.off("sync:done", onDone);
      socket.off("sync:error", onError);
      socket.off("sync:start", onStart);
    };
  }, [load]);

  const handleTrigger = async (month, year) => {
    setTriggering({ month, year });
    setFeedback(null);
    try {
      // Siempre sincronizamos compras + ventas (BOTH). Antes había un selector
      // pero simplificamos — sincronizar solo uno casi nunca sirve y duplica
      // los clicks del usuario.
      await PortalService.triggerSync({ month, year, direction: "BOTH" });
      setFeedback({
        kind: "ok",
        msg: `Sincronización iniciada para ${MONTHS[month - 1]} ${year}. Verás el progreso en vivo abajo.`,
      });
      setTimeout(load, 800);
    } catch (err) {
      const data = err?.response?.data;
      setFeedback({ kind: "error", msg: data?.error || err.message });
    } finally {
      setTriggering(null);
    }
  };

  const handleCancel = async (syncId) => {
    if (!window.confirm("¿Cancelar esta sincronización pegada? Podrás lanzar una nueva.")) return;
    try {
      await PortalService.cancelSync(syncId);
      setFeedback({ kind: "ok", msg: "Sincronización cancelada. Ya puedes lanzar una nueva." });
      await load();
    } catch (err) {
      setFeedback({ kind: "error", msg: err?.response?.data?.error || err.message });
    }
  };

  // Para cada (month, year), el sync más reciente en el historial.
  // Así podemos pintar la grilla "está / no está sincronizado".
  const latestByPeriod = useMemo(() => {
    const map = new Map();
    for (const s of history) {
      const m = s.period?.month;
      const y = s.period?.year;
      if (!m || !y) continue;
      const key = `${y}-${m}`;
      const prev = map.get(key);
      const ts = new Date(s.startedAt || s.createdAt || 0).getTime();
      if (!prev || ts > prev._ts) {
        map.set(key, { ...s, _ts: ts });
      }
    }
    return map;
  }, [history]);

  // Años a mostrar en el selector: actual ± lo que aparezca en el historial,
  // con un mínimo de 3 años hacia atrás para que el usuario pueda recuperar
  // períodos viejos sin pasar por admin.
  const yearOptions = useMemo(() => {
    const years = new Set([currentYear, currentYear - 1, currentYear - 2]);
    for (const s of history) {
      if (s.period?.year) years.add(s.period.year);
    }
    return Array.from(years).sort((a, b) => b - a);
  }, [history, currentYear]);

  // Estado por mes del año seleccionado.
  const monthStates = useMemo(() => {
    return MONTHS.map((_, idx) => {
      const month = idx + 1;
      const sync = latestByPeriod.get(`${selectedYear}-${month}`);
      const isFuture = selectedYear > currentYear || (selectedYear === currentYear && month > currentMonth);
      let kind;
      if (sync?.status === "COMPLETED") kind = "SYNCED";
      else if (sync?.status === "RUNNING" || sync?.status === "PENDING") kind = "RUNNING";
      else if (sync?.status === "FAILED") kind = "FAILED";
      else if (isFuture) kind = "FUTURE";
      else kind = "PENDING";
      return { month, sync, isFuture, kind };
    });
  }, [latestByPeriod, selectedYear, currentYear, currentMonth]);

  const syncedCount = monthStates.filter((m) => m.kind === "SYNCED").length;
  const pendingCount = monthStates.filter((m) => m.kind === "PENDING" || m.kind === "FAILED").length;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", marginBottom: 4 }}>
          Sincronizaciones SII
        </h1>
        <p style={{ fontSize: 14, color: "var(--text-secondary)" }}>
          Trae tus registros de compras y ventas del SII por mes. Marca con un check
          los meses ya sincronizados y resalta los que están pendientes.
          También corre automáticamente el día 5 de cada mes.
        </p>
      </div>

      {feedback && (
        <div
          style={{
            marginBottom: 16, padding: "10px 14px", borderRadius: 8, fontSize: 13,
            background: feedback.kind === "ok" ? "#ecfdf5" : "#fef2f2",
            border: `1px solid ${feedback.kind === "ok" ? "#bbf7d0" : "#fecaca"}`,
            color: feedback.kind === "ok" ? "#065f46" : "#991b1b",
          }}
        >
          {feedback.msg}
        </div>
      )}

      {/* Selector de año + grilla de meses */}
      <div style={{ ...CARD, padding: 20, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>Año:</span>
            <div style={{ display: "inline-flex", background: "var(--bg-hover)", borderRadius: 999, padding: 4, gap: 2 }}>
              {yearOptions.map((y) => (
                <button
                  key={y}
                  onClick={() => setSelectedYear(y)}
                  style={{
                    padding: "6px 14px", fontSize: 13, fontWeight: 600,
                    border: "none", borderRadius: 999, cursor: "pointer",
                    background: selectedYear === y ? "var(--bg-card)" : "transparent",
                    color: selectedYear === y ? "#0353a4" : "#64748b",
                    boxShadow: selectedYear === y ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  }}
                >
                  {y}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14, fontSize: 12, color: "var(--text-secondary)" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <CheckCircle size={14} color="#059669" /> {syncedCount} sincronizados
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <Clock size={14} color="#d97706" /> {pendingCount} pendientes
            </span>
            <button
              onClick={load}
              disabled={loading}
              style={{ background: "transparent", border: "none", color: "var(--accent)", cursor: "pointer", fontSize: 13, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              <RefreshCw size={13} className={loading ? "spin" : ""} /> Refrescar
            </button>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
            gap: 10,
          }}
        >
          {monthStates.map(({ month, sync, isFuture, kind }) => {
            const tile = MONTH_TILE[kind];
            const isTriggering = triggering && triggering.month === month && triggering.year === selectedYear;
            const isRunning = kind === "RUNNING";
            const canCancel = isRunning && sync?._id;
            const canTrigger = !isFuture && !isRunning && !isTriggering;
            return (
              <div
                key={month}
                style={{
                  position: "relative",
                  background: tile.bg,
                  border: `1.5px solid ${tile.border}`,
                  borderRadius: 10,
                  padding: 12,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  minHeight: 110,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>
                    {MONTHS[month - 1]}
                  </span>
                  <tile.Icon size={18} color={tile.fg} className={tile.spin ? "spin" : ""} />
                </div>
                <div style={{ fontSize: 11, fontWeight: 600, color: tile.fg, textTransform: "uppercase", letterSpacing: 0.3 }}>
                  {tile.label}
                </div>
                {sync && (
                  <div style={{ fontSize: 11, color: "var(--text-secondary)", lineHeight: 1.4 }}>
                    {kind === "SYNCED" && (
                      <>
                        {sync.result?.totalImported ?? 0} doc.{" · "}
                        {fmtDate(sync.completedAt || sync.startedAt)}
                      </>
                    )}
                    {isRunning && <>Iniciada {fmtDate(sync.startedAt || sync.createdAt)}</>}
                    {kind === "FAILED" && <>Último intento {fmtDate(sync.startedAt || sync.createdAt)}</>}
                  </div>
                )}
                {!sync && !isFuture && (
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Nunca sincronizado</div>
                )}
                {isFuture && (
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Aún no disponible</div>
                )}

                <div style={{ marginTop: "auto", display: "flex", gap: 6 }}>
                  {canTrigger && (
                    <button
                      onClick={() => handleTrigger(month, selectedYear)}
                      title={kind === "SYNCED" ? "Volver a sincronizar este mes" : "Sincronizar este mes"}
                      style={{
                        flex: 1,
                        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4,
                        padding: "6px 8px", fontSize: 12, fontWeight: 600,
                        background: kind === "SYNCED" ? "var(--bg-card)" : "#0353a4",
                        color: kind === "SYNCED" ? "#0353a4" : "#fff",
                        border: kind === "SYNCED" ? "1px solid #cbd5e1" : "none",
                        borderRadius: 6, cursor: "pointer",
                      }}
                    >
                      <Play size={11} /> {kind === "SYNCED" ? "Re-sync" : "Sincronizar"}
                    </button>
                  )}
                  {isTriggering && (
                    <button
                      disabled
                      style={{
                        flex: 1,
                        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4,
                        padding: "6px 8px", fontSize: 12, fontWeight: 600,
                        background: "#94a3b8", color: "#fff", border: "none", borderRadius: 6,
                        cursor: "not-allowed",
                      }}
                    >
                      <RefreshCw size={11} className="spin" /> Iniciando...
                    </button>
                  )}
                  {canCancel && (
                    <button
                      onClick={() => handleCancel(sync._id)}
                      title="Cancelar"
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        padding: "6px 8px", fontSize: 12, fontWeight: 600,
                        background: "var(--bg-card)", color: "var(--negative)",
                        border: "1px solid #fecaca", borderRadius: 6, cursor: "pointer",
                      }}
                    >
                      <X size={11} /> Cancelar
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {loading && history.length === 0 && (
          <div style={{ padding: 16, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
            Cargando historial...
          </div>
        )}
      </div>

      {/* Historial detallado (colapsado por defecto) */}
      <div style={{ ...CARD }}>
        <button
          onClick={() => setShowHistory((v) => !v)}
          style={{
            width: "100%", padding: "14px 20px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "transparent", border: "none", cursor: "pointer",
            borderBottom: showHistory ? "1px solid #f1f5f9" : "none",
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-secondary)" }}>
            Historial detallado ({history.length})
          </span>
          {showHistory ? <ChevronUp size={16} color="#64748b" /> : <ChevronDown size={16} color="#64748b" />}
        </button>

        {showHistory && (loading ? (
          <div style={{ padding: 32, textAlign: "center", color: "var(--text-muted)" }}>Cargando...</div>
        ) : history.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: "var(--text-muted)" }}>
            Aún no hay sincronizaciones. Lanza una arriba para empezar.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--bg-elevated)" }}>
                  <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", borderBottom: "2px solid var(--border)" }}>Período</th>
                  <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", borderBottom: "2px solid var(--border)" }}>Dirección</th>
                  <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", borderBottom: "2px solid var(--border)" }}>Estado</th>
                  <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", borderBottom: "2px solid var(--border)" }}>Origen</th>
                  <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", borderBottom: "2px solid var(--border)" }}>Importados</th>
                  <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", borderBottom: "2px solid var(--border)" }}>Inicio</th>
                  <th style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", borderBottom: "2px solid var(--border)" }}>Duración</th>
                  <th style={{ padding: "10px 12px", textAlign: "right", fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", borderBottom: "2px solid var(--border)" }}></th>
                </tr>
              </thead>
              <tbody>
                {history.map((s) => {
                  const st = STATUS_STYLE[s.status] || { bg: "#f1f5f9", fg: "#64748b", label: s.status, Icon: Clock };
                  return (
                    <tr key={s._id}>
                      <td style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-secondary)", borderBottom: "1px solid var(--border-light)" }}>
                        {MONTHS[(s.period?.month || 1) - 1]} {s.period?.year}
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-secondary)", borderBottom: "1px solid var(--border-light)" }}>
                        {s.direction === "BOTH" ? "Compras + ventas" : s.direction === "COMPRAS" ? "Compras" : "Ventas"}
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: 13, borderBottom: "1px solid var(--border-light)" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 10px", borderRadius: 999, background: st.bg, color: st.fg, fontSize: 12, fontWeight: 600 }}>
                          <st.Icon size={12} className={st.spin ? "spin" : ""} /> {st.label}
                        </span>
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: 12, color: "var(--text-secondary)", borderBottom: "1px solid var(--border-light)" }}>
                        {s.triggeredBy === "SCHEDULER" ? "Automático" : s.triggeredBy === "ADMIN" ? "Admin" : "Manual"}
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-primary)", textAlign: "right", borderBottom: "1px solid var(--border-light)", fontWeight: 600 }}>
                        {s.result?.totalImported ?? "—"}
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: 12, color: "var(--text-secondary)", borderBottom: "1px solid var(--border-light)" }}>
                        {fmtDate(s.startedAt || s.createdAt)}
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: 12, color: "var(--text-secondary)", borderBottom: "1px solid var(--border-light)" }}>
                        {fmtDuration(s.startedAt || s.createdAt, s.completedAt)}
                      </td>
                      <td style={{ padding: "10px 12px", textAlign: "right", borderBottom: "1px solid var(--border-light)" }}>
                        {(s.status === "PENDING" || s.status === "RUNNING") && (
                          <button
                            onClick={() => handleCancel(s._id)}
                            title="Cancelar sincronización pegada"
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              padding: "4px 10px", fontSize: 12,
                              border: "1px solid #fecaca", borderRadius: 6,
                              background: "var(--bg-card)", color: "var(--negative)", cursor: "pointer", fontWeight: 500,
                            }}
                          >
                            <X size={12} /> Cancelar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}
